import { round, sumBy } from 'lodash';
import { makeStyles } from 'tss-react/mui';
import {
  numberToCurrencyString,
  numberToDisplayString,
  safeSum,
} from 'further-ui/utils';
import { StagedShareholding } from '../ExitDetailsForm/types';

const useStyles = makeStyles()(() => ({
  tableFooter: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
  },
  tableFooterTd: {
    padding: '2rem 0 2rem 6px',
    fontWeight: 'bold',
  },
}));

type FooterShareholding = StagedShareholding & {
  sharesAllocatedForSale: number;
  totalToBeReturned?: number;
  netTaxableValue?: number;
};

type Props = {
  shareholdings: Array<FooterShareholding>;
  isBeingEdited?: boolean;
};

const ReviewShareholdingsTableFooter: React.FC<Props> = ({
  shareholdings,
  isBeingEdited,
}) => {
  const { classes } = useStyles();

  const tableFooterSumValues = {
    sharesAllocatedForSale: safeSum(
      shareholdings.map((sh) => sh.sharesAllocatedForSale),
    ),
    additionalGrossReceivedValue: sumBy(shareholdings, (sh) =>
      round(sh.additionalGrossReceivedValue ?? 0, 2),
    ),
    totalSaleValue: sumBy(shareholdings, (sh) =>
      round(sh.totalSaleValue ?? 0, 2),
    ),
    performanceFee: sumBy(shareholdings, (sh) =>
      round(sh.performanceFee ?? 0, 2),
    ),
    accruedFeesCharged: sumBy(shareholdings, (sh) =>
      round(sh.accruedFeesCharged ?? 0, 2),
    ),
    totalToBeReturned: sumBy(shareholdings, (sh) =>
      round(sh.totalToBeReturned ?? 0, 2),
    ),
    netTaxableValue: sumBy(shareholdings, (sh) =>
      round(sh.netTaxableValue ?? 0, 2),
    ),
  };

  return (
    <tbody>
      <tr className={classes.tableFooter}>
        <td className={classes.tableFooterTd} colSpan={4}></td>
        <td className={classes.tableFooterTd}>
          {numberToDisplayString(tableFooterSumValues.sharesAllocatedForSale, {
            maximumFractionDigits: 10,
          })}
        </td>
        <td className={classes.tableFooterTd}>
          {numberToCurrencyString(tableFooterSumValues.totalSaleValue)}
        </td>
        {isBeingEdited && (
          <td className={classes.tableFooterTd}>
            {numberToCurrencyString(
              tableFooterSumValues.additionalGrossReceivedValue,
            )}
          </td>
        )}
        <td className={classes.tableFooterTd}>
          {numberToCurrencyString(tableFooterSumValues.performanceFee)}
        </td>
        <td className={classes.tableFooterTd}>
          {numberToCurrencyString(tableFooterSumValues.accruedFeesCharged)}
        </td>
        <td className={classes.tableFooterTd}>
          {numberToCurrencyString(tableFooterSumValues.totalToBeReturned)}
        </td>
        <td className={classes.tableFooterTd}></td>
        <td className={classes.tableFooterTd}>
          {numberToCurrencyString(tableFooterSumValues.netTaxableValue)}
        </td>
      </tr>
    </tbody>
  );
};

export default ReviewShareholdingsTableFooter;
